import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Wallet, Plus, Download, IndianRupee, CheckCircle2, XCircle, Clock, Banknote, Calculator, CreditCard } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { DateRangePicker, rangeForPreset, DatePreset } from '@/components/ui/DateRangePicker';
import { CopyText } from '@/components/ui/CopyText';
import type { WalletSummary } from '@/components/layout/WalletBalanceMenu';

interface Txn {
  id: number; refNo: string; merchantId: number;
  amount: string; status: number; mode: string;
  paymentGateway?: string; pgTxnId?: string; email?: string;
  isPrepaidCredit: boolean; remarks?: string;
  createdAt: string; paidAt?: string;
}

/** ₹ with thousands separators, no paise. */
function inr(v: number | undefined | null): string {
  return '₹' + Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

const STATUS_LABEL: Record<number, { label: string; tone: string; Icon: any }> = {
  0: { label: 'Pending', tone: 'bg-amber-100 text-amber-800', Icon: Clock },
  1: { label: 'Success', tone: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
  2: { label: 'Failed',  tone: 'bg-rose-100 text-rose-700', Icon: XCircle },
};

export function WalletPage({ merchantPortal = false }: { merchantPortal?: boolean }) {
  // The header wallet menu deep-links here as /wallet?mode=online to show gateway recharges only.
  const [sp] = useSearchParams();
  const [status, setStatus] = useState('');
  const [mode, setMode] = useState(sp.get('mode') ?? '');
  const initial = rangeForPreset('last-month');
  const [start, setStart] = useState(initial.startDate);
  const [end, setEnd] = useState(initial.endDate);
  const [preset, setPreset] = useState<DatePreset>('last-month');
  const [showRecharge, setShowRecharge] = useState(false);

  // Same figures as the header pill — so the page you land on confirms the number you clicked.
  const { data: summary } = useQuery<WalletSummary | null>({
    queryKey: ['wallet-summary', merchantPortal ? 'merchant' : 'admin'],
    queryFn: async () => {
      try { return (await api.get<WalletSummary>('/wallet/summary')).data; } catch { return null; }
    },
    staleTime: 60 * 1000,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['wallet-txn', { status, mode, start, end }],
    queryFn: async () => {
      const params: any = { startDate: start, endDate: end, pageSize: 100 };
      if (status) params.status = status;
      if (mode) params.mode = mode;
      return (await api.get<{ total: number; items: Txn[] }>('/wallet/transactions', { params })).data;
    },
  });

  function exportCsv() {
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    if (mode) qs.set('mode', mode);
    if (start) qs.set('startDate', start);
    if (end) qs.set('endDate', end);
    const q = qs.toString();
    downloadBlob(`/wallet/transactions/export${q ? `?${q}` : ''}`, `wallet-transactions-${new Date().toISOString().slice(0,10)}.csv`);
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Wallet size={20} /> Wallet & Transactions</h1>
          <p className="text-sm text-slate-500">Recharges, manual credits and order debits</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50">
            <Download size={14} /> Export
          </button>
          {merchantPortal && (
            <button onClick={() => setShowRecharge(true)} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 flex items-center gap-1.5">
              <Plus size={14} /> Recharge Wallet
            </button>
          )}
        </div>
      </div>

      {/* The net position, with the arithmetic spelled out. Shown for admin (all merchants) and for a
          merchant (their own account) — same endpoint, same formula, scoped by who is asking. */}
      {summary?.shippingBalance && (
        <div className={`rounded-xl border p-4 mb-4 ${summary.shippingBalance.available < 0 ? 'border-rose-200 bg-rose-50/60' : 'border-emerald-200 bg-emerald-50/60'}`}>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500">
                {summary.scope === 'COMPANY'
                  ? `Net balance · ${summary.shippingBalance.merchantCount ?? summary.wallet.merchantCount} merchants`
                  : 'Your balance'}
              </div>
              <div className={`text-3xl font-bold font-mono ${summary.shippingBalance.available < 0 ? 'text-rose-700' : 'text-emerald-800'}`}>
                {inr(summary.shippingBalance.available)}
              </div>
              {/* Informational only — shipping is NOT restricted on balance, for prepaid or postpaid. */}
              {summary.shippingBalance.available < 0 && summary.scope === 'MERCHANT' && (
                <div className="text-xs text-rose-700 mt-0.5">Freight billed exceeds recharge + COD held by {inr(summary.shippingBalance.shortfall)}.</div>
              )}
            </div>
            {/*
              wallet balance + COD held = available.

              Freight is NOT a term here, and printing it as one is the bug this replaced: every
              freight row is already debited from `walletBalance` (see MerchantBalanceService — the
              backfill that posted freight into the wallet is why `available` stopped subtracting
              it), so the strip was showing `4,259 + 47,875 − 1,69,608 = 52,134`, an equation that
              does not hold. On a screen whose whole purpose is to show its working, an arithmetic
              line a merchant can disprove in their head costs more than the detail it added.
              Freight billed still appears, below, as the context it is.
            */}
            <div className="flex items-end gap-4 text-sm font-mono">
              <div>
                <div className="text-[10px] uppercase text-slate-500 font-sans">Wallet balance</div>
                <div className="text-slate-800">{inr(summary.shippingBalance.recharge)}</div>
              </div>
              <span className="text-slate-400 pb-0.5">+</span>
              <div>
                <div className="text-[10px] uppercase text-slate-500 font-sans">COD held</div>
                <div className="text-slate-800">{inr(summary.shippingBalance.remittanceHeld)}</div>
              </div>
              <span className="text-slate-400 pb-0.5">=</span>
              <div>
                <div className="text-[10px] uppercase text-slate-500 font-sans">Available</div>
                <div className={`font-semibold ${summary.shippingBalance.available < 0 ? 'text-rose-700' : 'text-emerald-800'}`}>
                  {inr(summary.shippingBalance.available)}
                </div>
              </div>
              <div className="pl-4 border-l border-slate-300/70">
                <div className="text-[10px] uppercase text-slate-500 font-sans">Freight billed (already debited)</div>
                <div className="text-slate-600">{inr(summary.shippingBalance.freight)}</div>
              </div>
            </div>
          </div>
          <div className="text-[10px] text-slate-500 mt-2">
            All-time position, not affected by the date filter below. <strong>Wallet balance</strong> is recharges and credits with every
            freight charge already taken off; <strong>COD held</strong> is remittance collected but not yet paid out to
            {summary.scope === 'COMPANY' ? ' merchants' : ' you'}; <strong>Freight billed</strong> is every shipment charged so far, shown for
            reference — it is not subtracted a second time.
            This figure is for visibility only — shipping is not restricted on balance, for prepaid or postpaid accounts.
          </div>
        </div>
      )}

      {/* Wallet + the three drill-downs, mirroring the header pill */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-xl p-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1"><Wallet size={12} /> {summary.scope === 'COMPANY' ? `Recharge on account · ${summary.wallet.merchantCount} merchants` : 'Recharge on account'}</div>
            <div className="text-xl font-bold text-slate-900 font-mono">{inr(summary.wallet.balance)}</div>
            {summary.scope === 'MERCHANT' && summary.wallet.billingMode === 'POSTPAID' && (
              <div className="text-[11px] text-slate-500">Postpaid limit {inr(summary.wallet.postpaidLimit)}</div>
            )}
          </div>
          <Link to="/remittance" className="bg-white border border-slate-200 rounded-xl p-3 hover:border-emerald-300 hover:shadow-sm transition">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1"><Banknote size={12} className="text-emerald-600" /> Total remittance</div>
            <div className="text-xl font-bold text-slate-900 font-mono">{inr(summary.remittance.total)}</div>
            <div className="text-[11px] text-slate-500">{inr(summary.remittance.pending)} pending · {summary.remittance.count.toLocaleString()} shipments</div>
          </Link>
          <Link to="/freight" className="bg-white border border-slate-200 rounded-xl p-3 hover:border-indigo-300 hover:shadow-sm transition">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1"><Calculator size={12} className="text-indigo-600" /> Total freight</div>
            <div className="text-xl font-bold text-slate-900 font-mono">{inr(summary.freight.total)}</div>
            <div className="text-[11px] text-slate-500">{summary.freight.count.toLocaleString()} shipments billed</div>
          </Link>
          <button type="button" onClick={() => setMode('online')} className="text-left bg-white border border-slate-200 rounded-xl p-3 hover:border-violet-300 hover:shadow-sm transition">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1"><CreditCard size={12} className="text-violet-600" /> Recharge amount</div>
            <div className="text-xl font-bold text-slate-900 font-mono">{inr(summary.recharge.total)}</div>
            <div className="text-[11px] text-slate-500">{inr(summary.recharge.online)} gateway · {inr(summary.recharge.manual)} manual</div>
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-3 mb-4 grid grid-cols-4 gap-3 items-end">
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="input w-full">
            <option value="">All</option>
            <option value="1">Success</option>
            <option value="0">Pending</option>
            <option value="2">Failed</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Mode</label>
          <select value={mode} onChange={(e) => setMode(e.target.value)} className="input w-full">
            <option value="">All</option>
            <option value="online">Online (PayU)</option>
            <option value="manual">Manual</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="text-xs text-slate-500 mb-1 block">Date Range</label>
          <DateRangePicker value={{ startDate: start, endDate: end }} defaultPreset={preset}
            onChange={(r) => { setStart(r.startDate); setEnd(r.endDate); setPreset(r.preset); }} />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Ref / PG Txn</th>
                {/* A merchant only ever gets its OWN rows back, so the column is noise there. */}
                {!merchantPortal && <th className="text-left px-4 py-2.5">Merchant</th>}
                <th className="text-right px-4 py-2.5">Amount</th>
                <th className="text-left px-4 py-2.5">Mode</th>
                <th className="text-left px-4 py-2.5">Type</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Created</th>
                <th className="text-left px-4 py-2.5">Paid</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (<tr><td colSpan={merchantPortal ? 7 : 8} className="text-center py-10 text-slate-400"><Loader2 size={20} className="mx-auto animate-spin" /></td></tr>)}
              {!isLoading && (data?.items ?? []).length === 0 && (<tr><td colSpan={merchantPortal ? 7 : 8} className="text-center py-12 text-slate-400">No transactions for these filters.</td></tr>)}
              {(data?.items ?? []).map(t => {
                const st = STATUS_LABEL[t.status] ?? STATUS_LABEL[0];
                return (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="px-4 py-2 text-xs">
                      <div className="font-mono"><CopyText value={t.refNo} display={<span>{t.refNo}</span>} iconSize={10} silent /></div>
                      {t.pgTxnId && <div className="text-slate-500 font-mono">PG: {t.pgTxnId}</div>}
                    </td>
                    {!merchantPortal && <td className="px-4 py-2 text-xs">#{t.merchantId} {t.email && <div className="text-slate-500">{t.email}</div>}</td>}
                    <td className="px-4 py-2 text-right font-mono font-medium">₹{Number(t.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-2 text-xs uppercase">{t.mode}{t.paymentGateway && ` · ${t.paymentGateway}`}</td>
                    <td className="px-4 py-2 text-xs">{t.isPrepaidCredit ? <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">Wallet credit</span> : <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">Postpaid limit</span>}</td>
                    <td className="px-4 py-2 text-xs">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${st.tone}`}>
                        <st.Icon size={10} /> {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600">{new Date(t.createdAt).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-2 text-xs text-slate-600">{t.paidAt ? new Date(t.paidAt).toLocaleString('en-IN') : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showRecharge && (
        <Modal isOpen={true} onClose={() => setShowRecharge(false)} title="Recharge Wallet" subtitle="Pay via PayU" maxWidth="md">
          <RechargeDialog onClose={() => setShowRecharge(false)} onPaid={() => { setShowRecharge(false); refetch(); }} />
        </Modal>
      )}
    </div>
  );
}

function RechargeDialog({ onClose, onPaid }: { onClose: () => void; onPaid: () => void }) {
  const [amount, setAmount] = useState('');
  const init = useMutation({
    mutationFn: async () => (await api.post('/wallet/payu/init', { amount: Number(amount) })).data,
    onSuccess: (data: any) => {
      const w = window.open('', '_blank', 'width=720,height=720');
      if (!w) { toast.error('Pop-up blocked — allow pop-ups for this site'); return; }
      w.document.open();
      w.document.write(data.formHtml);
      w.document.close();
      toast.success('Redirecting to PayU…');
      onPaid();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to start payment'),
  });

  const quickAmounts = [500, 1000, 2500, 5000, 10000];

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-medium uppercase text-slate-500 mb-1.5 block">Amount (INR)</label>
        <div className="relative">
          <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="500"
            className="input w-full pl-9 text-lg font-mono" min={1} step={0.01} />
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {quickAmounts.map(a => (
          <button key={a} onClick={() => setAmount(String(a))}
            className="px-3 py-1.5 border border-slate-200 rounded text-sm hover:bg-slate-50">₹{a.toLocaleString('en-IN')}</button>
        ))}
      </div>
      <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2.5">
        You'll be redirected to PayU's secure payment page. On success, your wallet will be credited automatically.
      </div>
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded">Cancel</button>
        <button onClick={() => init.mutate()} disabled={!amount || Number(amount) <= 0 || init.isPending}
          className="px-5 py-2 bg-primary text-white rounded text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
          {init.isPending ? <Loader2 size={14} className="animate-spin" /> : null} Pay ₹{amount || 0}
        </button>
      </div>
    </div>
  );
}

/**
 * Merchant wallet.
 *
 * An account created by a **Shopify App Store install** never reaches the wallet: our Shopify app is
 * listed as free and App Store requirement 1.2.1 forbids charging those merchants outside Shopify
 * Billing. The nav entry is already hidden for them — this closes the direct URL as well, so typing
 * `/wallet` cannot put a payment form in front of a reviewer. The recharge APIs refuse them too, so
 * all three layers agree.
 */
export function MerchantWalletPage() {
  const { data: me, isLoading } = useQuery({
    queryKey: ['merchant', 'me', 'profile'],
    queryFn: async () => { try { return (await api.get('/merchants/me/profile')).data as any; } catch { return null; } },
    staleTime: 60_000,
  });
  if (isLoading) return <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-primary" /></div>;
  if (me?.shopifyAppInstall) {
    return (
      <div className="max-w-lg mx-auto mt-10 bg-white border border-slate-200 rounded-xl p-6 text-center">
        <div className="text-base font-semibold text-slate-800">This app is free</div>
        <p className="text-sm text-slate-500 mt-2">
          Your account was created through the Shopify App Store, so there is nothing to pay for here —
          connecting your store, syncing orders and pushing tracking back all cost nothing.
        </p>
        <p className="text-sm text-slate-500 mt-2">
          To arrange shipping and freight terms, please contact our team and we&apos;ll set your account up.
        </p>
      </div>
    );
  }
  return <WalletPage merchantPortal />;
}
export function AdminWalletPage() { return <WalletPage merchantPortal={false} />; }
